import React, { useMemo, useState } from 'react';
import {
  Armchair,
  ChevronDown,
  ChevronRight,
  Luggage,
  MessageCircle,
  Plane,
  PlaneTakeoff,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import ActionModalShell from '@/components/ui/ActionModalShell';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

const TRIGGER_CLASS =
  'h-8 sm:h-7 px-3 sm:px-2.5 text-[11px] font-normal text-white/50 hover:text-white/80 hover:bg-white/5 gap-1 rounded-full touch-manipulation';

const DROPDOWN_PANEL_CLASS =
  'z-[80] p-2 min-w-[min(100vw-2rem,300px)] bg-[#0b0b10] border border-white/10 text-white rounded-2xl ' +
  'shadow-[0_24px_80px_-24px_rgba(0,0,0,0.8)] overflow-hidden';

function FlightOptionRowContent({ icon: Icon, label, description, destructive }) {
  return (
    <>
      <div
        className={cn(
          'w-9 h-9 rounded-xl flex items-center justify-center border flex-shrink-0',
          destructive
            ? 'bg-red-950/30 border-red-900/35 text-red-300'
            : 'bg-brand-800/25 border-brand-700/30 text-brand-200/90',
        )}
      >
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0 text-left">
        <div
          className={cn(
            'text-sm font-medium leading-tight',
            destructive ? 'text-red-300' : 'text-white/90',
          )}
        >
          {label}
        </div>
        {description && (
          <div className="text-white/60 text-xs font-normal mt-0.5 leading-snug">{description}</div>
        )}
      </div>
      <ChevronRight
        className={cn(
          'w-4 h-4 flex-shrink-0 transition-colors',
          destructive ? 'text-red-400/30' : 'text-white/25',
        )}
      />
    </>
  );
}

function FlightOptionButton({ icon, label, description, destructive, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-3.5 py-3 rounded-xl border transition-colors group text-left touch-manipulation min-h-[52px]',
        destructive
          ? 'bg-red-950/25 hover:bg-red-950/40 border-red-900/35'
          : 'bg-white/[0.02] hover:bg-white/[0.05] border-white/[0.08]',
      )}
    >
      <FlightOptionRowContent
        icon={icon}
        label={label}
        description={description}
        destructive={destructive}
      />
    </button>
  );
}

export default function FlightOptionsMenu({
  changeFlightEligible,
  onChangeFlight,
  onRequestChange,
  onPurchaseSeats,
  onPurchaseBaggage,
  showChangeSeatSelection,
  onChangeSeatSelection,
  onCancelFlight,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const items = useMemo(() => {
    const list = [];

    if (changeFlightEligible) {
      list.push({
        id: 'change-flight',
        icon: PlaneTakeoff,
        label: 'Change Flight',
        description: 'Modify dates or routing',
        onClick: onChangeFlight,
      });
    } else {
      list.push({
        id: 'request-change',
        icon: MessageCircle,
        label: 'Request Flight Change',
        description: 'Contact support to change your trip',
        onClick: onRequestChange,
      });
    }

    list.push({
      id: 'purchase-seats',
      icon: Armchair,
      label: 'Purchase Seats',
      description: 'Choose seats on the live seat map',
      onClick: onPurchaseSeats,
    });

    list.push({
      id: 'purchase-baggage',
      icon: Luggage,
      label: 'Purchase Baggage',
      description: 'Add checked bags to your booking',
      onClick: onPurchaseBaggage,
    });

    if (showChangeSeatSelection) {
      list.push({
        id: 'change-seat',
        icon: Armchair,
        label: 'Change Seat Selection',
        description: 'Update seats already on your booking',
        onClick: onChangeSeatSelection,
      });
    }

    list.push({
      id: 'cancel-flight',
      icon: XCircle,
      label: 'Cancel Flight',
      destructive: true,
      onClick: onCancelFlight,
    });

    return list;
  }, [
    changeFlightEligible,
    onCancelFlight,
    onChangeFlight,
    onChangeSeatSelection,
    onPurchaseBaggage,
    onPurchaseSeats,
    onRequestChange,
    showChangeSeatSelection,
  ]);

  const handleSelect = (item) => {
    setOpen(false);
    item.onClick();
  };

  const triggerClassName = cn(
    TRIGGER_CLASS,
    disabled && 'opacity-40 cursor-not-allowed pointer-events-none',
  );

  const trigger = (
    <Button
      variant="ghost"
      size="sm"
      className={triggerClassName}
      disabled={disabled}
      aria-disabled={disabled}
    >
      <span>Flight Options</span>
      <ChevronDown className="w-3 h-3" />
    </Button>
  );

  if (disabled) {
    return trigger;
  }

  if (isMobile) {
    return (
      <>
        <Button
          variant="ghost"
          size="sm"
          className={TRIGGER_CLASS}
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <span>Flight Options</span>
          <ChevronDown className="w-3 h-3" />
        </Button>
        <ActionModalShell
          isOpen={open}
          onClose={() => setOpen(false)}
          icon={Plane}
          title="Flight Options"
          description="Manage your trip"
          maxWidthClass="sm:max-w-md"
          mobilePlacement="center"
        >
          <div className="space-y-2">
            {items.map((item) => (
              <FlightOptionButton
                key={item.id}
                icon={item.icon}
                label={item.label}
                description={item.description}
                destructive={item.destructive}
                onClick={() => handleSelect(item)}
              />
            ))}
          </div>
        </ActionModalShell>
      </>
    );
  }

  const regularItems = items.filter((item) => !item.destructive);
  const destructiveItems = items.filter((item) => item.destructive);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        side="bottom"
        sideOffset={8}
        collisionPadding={{ top: 80, bottom: 16, left: 12, right: 12 }}
        className={DROPDOWN_PANEL_CLASS}
      >
        <div className="px-2 pt-1 pb-2">
          <p className="text-[10px] uppercase tracking-widest text-white/40 font-normal">Trip actions</p>
        </div>
        <div className="flex flex-col gap-1 px-1 pb-1">
          {regularItems.map((item) => (
            <DropdownMenuItem
              key={item.id}
              onSelect={() => handleSelect(item)}
              className="cursor-pointer p-0 focus:bg-transparent rounded-xl"
            >
              <div className="w-full flex items-center gap-3 px-2.5 py-2.5 min-h-[44px] rounded-xl hover:bg-white/[0.05] focus:bg-white/[0.05] transition-colors">
                <FlightOptionRowContent
                  icon={item.icon}
                  label={item.label}
                  description={item.description}
                  destructive={false}
                />
              </div>
            </DropdownMenuItem>
          ))}
        </div>
        {destructiveItems.length > 0 && (
          <>
            <DropdownMenuSeparator className="bg-white/[0.06] my-1" />
            <div className="flex flex-col gap-1 px-1 pb-1">
              {destructiveItems.map((item) => (
                <DropdownMenuItem
                  key={item.id}
                  onSelect={() => handleSelect(item)}
                  className="cursor-pointer p-0 focus:bg-transparent rounded-xl"
                >
                  <div className="w-full flex items-center gap-3 px-2.5 py-2.5 min-h-[44px] rounded-xl hover:bg-red-950/30 focus:bg-red-950/30 transition-colors">
                    <FlightOptionRowContent
                      icon={item.icon}
                      label={item.label}
                      description={item.description}
                      destructive
                    />
                  </div>
                </DropdownMenuItem>
              ))}
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
